<script>
/**
 * @description Pantalla de perfil del usuario autenticado.
 * Permite ver datos personales, editar email/teléfono y (para médicos)
 * descripción profesional, foto de perfil y foto de portada.
 * Los cambios se persisten via userService.updateMe() y uploads multipart.
 */
import userService from "@/services/user";
import api from "@/services/api";
import { getPublicFileUrl } from "@/services/config";
import { mapGetters } from "vuex";
import helperService from "@/services/helper";

export default {
  name: "Profile",

  data() {
    return {
      isUpdating: false,
      /** @description Loading de upload de foto de perfil */
      isUploadingPhoto: false,
      /** @description Loading de upload de cover */
      isUploadingCover: false,
      /** @description Preview local de foto de perfil (Data URL) */
      photoPreview: null,
      /** @description Preview local de foto de portada (Data URL) */
      coverPreview: null,
      form: {
        email: "",
        phone: "",
        governmentId: { type: "dni", number: "" },
        /** @description Descripción profesional del médico */
        description: "",
        /** @description UC-REG-302 — Área / Servicio del médico (encabezado de los PDFs) */
        service: "",
      },
      originalForm: {
        email: "",
        phone: "",
        governmentId: { type: "dni", number: "" },
        description: "",
        service: "",
      },
    };
  },

  created() {
    this.form.email = this.user.email || "";
    this.form.phone = this.user.phone || "";
    this.form.governmentId = {
      type: this.user.governmentId?.type || "dni",
      number: this.user.governmentId?.number || "",
    };
    /** @description Cargar descripción existente del médico al inicializar el formulario */
    this.form.description = this.user?.doctor?.description || "";
    this.form.service = this.user?.doctor?.service || "";
    this.originalForm = JSON.parse(JSON.stringify(this.form));
  },

  computed: {
    ...mapGetters(["user"]),

    /** @returns {string} Nombre completo del usuario */
    fullName() {
      return helperService.getFullName(this.user);
    },

    /** @returns {string} Iniciales para el avatar */
    initials() {
      const first = (this.user.firstName || "").charAt(0).toUpperCase();
      const last = (this.user.lastName || "").charAt(0).toUpperCase();
      return `${first}${last}` || "?";
    },

    /** @returns {string} Rol formateado */
    roleName() {
      const roles = {
        doctor: "Médico",
        admin: "Administrador",
        manager: "Manager",
        secretary: "Secretaria",
        "health-admin": "Admin. Salud",
        patient: "Paciente",
      };
      return roles[this.user.role] || this.user.role || "—";
    },

    /** @returns {string} DNI */
    governmentId() {
      if (this.user.governmentId?.number) {
        const type = (this.user.governmentId.type || "DNI").toUpperCase();
        return `${type} ${this.user.governmentId.number}`;
      }
      return "—";
    },

    /**
     * @description Si el usuario es médico
     * @returns {boolean}
     */
    isDoctor() {
      return this.user.type === "doctor" || !!this.user.doctor;
    },

    /**
     * @description URL actual de foto de perfil: preview local o S3 key del avatar guardado
     * @returns {string|null}
     */
    currentPhoto() {
      if (this.photoPreview) return this.photoPreview;
      return getPublicFileUrl(this.user?.avatar?.key);
    },

    /**
     * @description URL actual de foto de portada: preview local o URL del cover guardado
     * @returns {string|null}
     */
    currentCover() {
      if (this.coverPreview) return this.coverPreview;
      return getPublicFileUrl(this.user?.doctor?.coverPhoto?.key);
    },

    /**
     * @description Slug de turnera del médico (doctor.slug del store)
     * @returns {string|null}
     */
    bookingSlug() {
      return this.user?.doctor?.slug || null;
    },

    /**
     * @description URL pública completa de la turnera del médico
     * @returns {string|null}
     */
    bookingUrl() {
      if (!this.bookingSlug) return null;
      const base = window.location.origin;
      return `${base}/booking/${this.bookingSlug}`;
    },

    /**
     * @returns {boolean} Si hay cambios pendientes
     */
    hasChanges() {
      return (
        this.form.email !== this.originalForm.email ||
        this.form.phone !== this.originalForm.phone ||
        this.form.governmentId.number !==
          this.originalForm.governmentId.number ||
        this.form.description !== this.originalForm.description ||
        this.form.service !== this.originalForm.service
      );
    },
  },

  methods: {
    /**
     * @description Guarda los cambios de email, teléfono y descripción profesional (médicos).
     * El campo description se anida dentro de doctor para que updateMe lo persista correctamente.
     */
    /**
     * @description Guarda cambios de email, teléfono, documento y descripción profesional.
     * Llama a PUT /users/me (accessible por cualquier rol autenticado).
     */
    async saveProfile() {
      if (!this.hasChanges || this.isUpdating) return;
      this.isUpdating = true;
      try {
        const payload = {
          email: this.form.email,
          phone: this.form.phone,
          // Solo mandar governmentId si el número tiene valor — el schema requiere number
          ...(this.form.governmentId.number
            ? { governmentId: this.form.governmentId }
            : {}),
          ...(this.isDoctor
            ? {
                doctor: {
                  ...this.user.doctor,
                  description: this.form.description,
                  // UC-REG-302
                  service: this.form.service.trim(),
                },
              }
            : {}),
        };
        await userService.updateMe(payload);
        this.originalForm = JSON.parse(JSON.stringify(this.form));
        this.$message.success("Perfil actualizado con éxito");
      } catch {
        this.$message.error("Error al actualizar el perfil");
      } finally {
        this.isUpdating = false;
      }
    },

    /** @description Descarta los cambios */
    cancelChanges() {
      this.form = JSON.parse(JSON.stringify(this.originalForm));
    },

    /**
     * @description Lee un archivo de imagen y muestra preview local
     * @param {File} file
     * @param {'photo'|'cover'} target
     */
    previewImage(file, target) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        if (target === "photo") this.photoPreview = e.target.result;
        else this.coverPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    /**
     * @description Maneja selección de foto de perfil desde input file
     * @param {Event} event
     */
    onPhotoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.previewImage(file, "photo");
      this.uploadPhoto(file);
    },

    /**
     * @description Maneja selección de foto de portada desde input file
     * @param {Event} event
     */
    onCoverSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.previewImage(file, "cover");
      this.uploadCover(file);
    },

    /**
     * @description Sube foto de perfil via PUT /users/:id/avatar (campo: avatar)
     * @param {File} file
     * @returns {Promise<void>}
     */
    async uploadPhoto(file) {
      this.isUploadingPhoto = true;
      try {
        const formData = new FormData();
        formData.append("avatar", file);
        const res = await api.put(`/users/${this.user._id}/avatar`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.$store.commit("setUser", {
          ...this.user,
          avatar: res.data?.avatar,
        });
        this.$message.success("Foto de perfil actualizada");
      } catch {
        this.$message.error("Error al subir la foto de perfil");
        this.photoPreview = null;
      } finally {
        this.isUploadingPhoto = false;
      }
    },

    /**
     * @description Sube foto de portada via PUT /users/:id/cover (campo: cover)
     * @param {File} file
     * @returns {Promise<void>}
     */
    async uploadCover(file) {
      this.isUploadingCover = true;
      try {
        const formData = new FormData();
        formData.append("cover", file);
        const res = await api.put(`/users/${this.user._id}/cover`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const updatedUser = {
          ...this.user,
          doctor: {
            ...this.user.doctor,
            coverPhoto: res.data?.doctor?.coverPhoto,
          },
        };
        this.$store.commit("setUser", updatedUser);
        this.$message.success("Foto de portada actualizada");
      } catch {
        this.$message.error("Error al subir la foto de portada");
        this.coverPreview = null;
      } finally {
        this.isUploadingCover = false;
      }
    },

    /** @description Dispara el input file de foto de perfil */
    triggerPhotoInput() {
      this.$refs.photoInput.click();
    },

    /** @description Dispara el input file de portada */
    triggerCoverInput() {
      this.$refs.coverInput.click();
    },

    /** @description Copia la URL de turnera al portapapeles */
    copyBookingUrl() {
      if (!this.bookingUrl) return;
      navigator.clipboard.writeText(this.bookingUrl).then(() => {
        this.$message.success("Link copiado al portapapeles");
      });
    },
  },
};
</script>

<template lang="pug">
section.profile-page
  .profile-card
    .profile-card__header
      .profile-avatar
        span {{ initials }}
      .profile-identity
        h1.profile-identity__name {{ fullName }}
        .profile-identity__role {{ roleName }}

    .profile-card__body
      .profile-section
        h3.profile-section__title Información personal
        .profile-info-grid
          .profile-info-item
            span.profile-info-item__label Nombre
            span.profile-info-item__value {{ user.firstName || '—' }}
          .profile-info-item
            span.profile-info-item__label Apellido
            span.profile-info-item__value {{ user.lastName || '—' }}
          .profile-info-item
            span.profile-info-item__label Documento
            span.profile-info-item__value {{ governmentId }}
          .profile-info-item
            span.profile-info-item__label Nombre de usuario
            span.profile-info-item__value {{ user.username || '—' }}

      .profile-divider

      .profile-section
        h3.profile-section__title Datos de contacto
        p.profile-section__subtitle Podés actualizar tu email, teléfono y documento
        .profile-form-grid
          .profile-form-group
            label.profile-form-group__label Email
            input.profile-form-group__input(
              v-model="form.email"
              type="email"
              placeholder="tu@email.com"
            )
          .profile-form-group
            label.profile-form-group__label Teléfono
            input.profile-form-group__input(
              v-model="form.phone"
              type="tel"
              placeholder="Ej: 1155667788"
            )
          .profile-form-group
            label.profile-form-group__label Documento (DNI)
            input.profile-form-group__input(
              v-model="form.governmentId.number"
              type="text"
              placeholder="Sin puntos ni espacios"
            )

      template(v-if="bookingUrl")
        .profile-divider
        .profile-section
          h3.profile-section__title Tu turnera online
          p.profile-section__subtitle Compartí este link con tus pacientes para que reserven turnos
          .profile-booking-link
            span.profile-booking-link__url {{ bookingUrl }}
            button.profile-btn.profile-btn--secondary(@click="copyBookingUrl") Copiar link

      template(v-if="isDoctor")
        .profile-divider

        //- Fotos públicas (solo médicos)
        .profile-section
          h3.profile-section__title Fotos de tu perfil público
          p.profile-section__subtitle Estas imágenes aparecen en tu turnera online

          //- Foto de portada (cover)
          input(
            ref="coverInput"
            type="file"
            accept="image/*"
            style="display:none"
            @change="onCoverSelected"
          )
          .profile-cover-wrap
            .profile-cover(
              :style="currentCover ? { backgroundImage: `url(${currentCover})` } : {}"
              @click="triggerCoverInput"
            )
              .profile-cover__overlay
                span.material-icons-outlined(v-if="!isUploadingCover") add_photo_alternate
                span.material-icons-outlined.profile-cover__spinner(v-else) sync
                span.profile-cover__label {{ isUploadingCover ? 'Subiendo...' : (currentCover ? 'Cambiar portada' : 'Subir foto de portada') }}

          //- Foto de perfil
          input(
            ref="photoInput"
            type="file"
            accept="image/*"
            style="display:none"
            @change="onPhotoSelected"
          )
          .profile-photo-row
            .profile-photo-avatar(
              :style="currentPhoto ? { backgroundImage: `url(${currentPhoto})` } : {}"
              @click="triggerPhotoInput"
            )
              span(v-if="!currentPhoto") {{ initials }}
              .profile-photo-avatar__overlay
                span.material-icons-outlined(v-if="!isUploadingPhoto") photo_camera
                span.material-icons-outlined.profile-cover__spinner(v-else) sync
            .profile-photo-hint
              p.profile-photo-hint__title Foto de perfil
              p.profile-photo-hint__sub Recomendado: cuadrada, mínimo 200×200px

        .profile-divider
        .profile-section
          h3.profile-section__title Descripción profesional
          p.profile-section__subtitle Aparece en tu turnera online
          .profile-form-group(style="margin-top: 14px")
            label.profile-form-group__label Descripción
            textarea.profile-form-group__input.profile-form-group__textarea(
              v-model="form.description"
              placeholder="Contá tu especialización, experiencia, servicios que ofrecés..."
              rows="4"
            )
          //- UC-REG-302 — Área / Servicio: se imprime bajo la especialidad en los PDFs.
          .profile-form-group(style="margin-top: 14px")
            label.profile-form-group__label Área / Servicio
            input.profile-form-group__input.profile-service-input(
              type="text"
              v-model="form.service"
              maxlength="120"
              placeholder="Ej. Servicio de Cardiología — Hospital San José"
            )
            p.profile-section__subtitle(style="margin-top: 4px") Aparece en el encabezado de tus recetas y órdenes.

      .profile-actions(v-if="hasChanges")
        button.profile-btn.profile-btn--secondary(@click="cancelChanges") Cancelar
        button.profile-btn.profile-btn--primary(
          @click="saveProfile"
          :disabled="isUpdating"
        )
          span(v-if="!isUpdating") Guardar cambios
          span(v-else) Guardando...
</template>

<style lang="scss" scoped>
.profile-page {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.profile-card {
  width: 100%;
  max-width: 640px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.profile-card__header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 28px 32px;
  background: linear-gradient(135deg, #002d5e 0%, #003d7a 100%);
  color: white;
}

.profile-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-size: 22px;
    font-weight: 700;
    color: white;
  }
}

.profile-identity__name {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}

.profile-identity__role {
  font-size: 13px;
  font-weight: 500;
  opacity: 0.75;
  margin-top: 2px;
}

.profile-card__body {
  padding: 28px 32px;
}

.profile-section {
  margin-bottom: 4px;
}

.profile-section__title {
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 4px 0;
}

.profile-section__subtitle {
  font-size: 12px;
  color: #94a3b8;
  margin: 0 0 16px 0;
}

.profile-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 14px;
}

.profile-info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.profile-info-item__label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.profile-info-item__value {
  font-size: 14px;
  font-weight: 500;
  color: #002d5e;
}

.profile-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 24px 0;
}

.profile-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.profile-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.profile-form-group__label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.profile-form-group__input {
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 14px;
  font-family: inherit;
  color: #002d5e;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: #ffffff;
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
  }
}

/** Textarea de descripción profesional del médico */
.profile-form-group__textarea {
  resize: vertical;
  min-height: 90px;
  line-height: 1.5;
}

.profile-booking-link {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f0f4f8;
  border: 1.5px solid #d1dce8;
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 14px;

  &__url {
    flex: 1;
    font-size: 13px;
    color: #002d5e;
    font-family: monospace;
    word-break: break-all;
  }
}

.profile-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.profile-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 0.2s;
}

.profile-btn--secondary {
  background: #ffffff;
  color: #64748b;
  border: 1.5px solid #e2e8f0;

  &:hover {
    border-color: #94a3b8;
    color: #002d5e;
  }
}

.profile-btn--primary {
  background: #002d5e;
  color: white;

  &:hover {
    background: #003d7a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* ── Foto de portada ── */
.profile-cover-wrap {
  margin-top: 16px;
  margin-bottom: 20px;
}

.profile-cover {
  height: 160px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1a4480, #0d7a9d);
  background-size: cover;
  background-position: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #d1dce8;
  transition: border-color 0.2s;

  &:hover {
    border-color: #002d5e;

    .profile-cover__overlay {
      opacity: 1;
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 45, 94, 0.55);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    opacity: 0.8;
    transition: opacity 0.2s;

    .material-icons-outlined {
      font-size: 28px;
      color: #fff;
    }
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
  }

  &__spinner {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ── Foto de perfil ── */
.profile-photo-row {
  display: flex;
  align-items: center;
  gap: 18px;
}

.profile-photo-avatar {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  background: linear-gradient(135deg, #002d5e, #1e6fa8);
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #e2e8f0;
  transition: border-color 0.2s;

  &:hover {
    border-color: #002d5e;

    .profile-photo-avatar__overlay {
      opacity: 1;
    }
  }

  > span:first-child {
    font-size: 26px;
    font-weight: 700;
    color: white;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 45, 94, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;

    .material-icons-outlined {
      font-size: 24px;
      color: #fff;
    }
  }
}

.profile-photo-hint {
  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #002d5e;
    margin: 0 0 4px;
  }

  &__sub {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
  }
}
</style>
